/**
 * Tenant Management API Service
 * 
 * Placeholder implementations for Vivre Control Plane tenant endpoints.
 * Replace with real API calls when backend is ready.
 */

import axiosInstance from './axiosInstance';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_TENANTS = [];

const MOCK_TENANT_DETAIL = {
  id: '664a1b2c3d4e5f6a7b8c9d01',
  name: 'Acme Corp',
  status: 'active',
  plan: 'business',
  tenantId: 'T-001',
  contact: {
    name: 'John Smith',
    email: 'admin@acmecorp.com',
    phone: '+1-555-0100',
  },
  billing: {
    contactName: 'Jane Doe',
    contactEmail: 'billing@acmecorp.com',
    contactAddress: '123 Business Ave, Suite 400',
    country: 'US',
    duration: 'annually',
  },
  legalEntity: {
    name: 'Acme Corporation Inc.',
    address: '123 Business Ave, Suite 400, New York, NY 10001',
    country: 'US',
    pincode: '10001',
  },
  metadata: {
    productType: 'OTT Platform',
    createdAt: '2024-08-15T10:30:00Z',
    lastModified: '2025-05-20T14:22:00Z',
    createdBy: 'admin@vivre.io',
    lastModifiedBy: 'ops@vivre.io',
  },
  lifecycle: {
    currentState: 'active',
    stateHistory: [
      {
        fromState: null,
        toState: 'provisioning',
        timestamp: '2024-08-15T10:30:00Z',
        triggeredBy: 'admin@vivre.io',
        reason: 'New tenant onboarding',
      },
      {
        fromState: 'provisioning',
        toState: 'active',
        timestamp: '2024-08-15T10:45:00Z',
        triggeredBy: 'system',
        reason: 'Provisioning completed successfully',
      },
    ],
    provisionedAt: '2024-08-15T10:45:00Z',
    suspendedAt: null,
    offboardedAt: null,
  },
};

// ─── Helper: Simulate API delay ──────────────────────────────────────────────

const delay = (ms = 500) => new Promise((resolve) => setTimeout(resolve, ms));

// ─── Tenant API Functions ────────────────────────────────────────────────────

/**
 * GET /v1/tenants
 * List tenants with search, filter, sort, and pagination
 */
export const getAll = async (params = {}) => {
  const { search = '', status = null, plan = null, sortBy = 'createdAt', sortOrder = 'desc', page = 1, pageSize = 25 } = params;

  // TODO: Replace with real API call
  // return axiosInstance.get('/tenants', { params });

  await delay(400);

  let filtered = [...MOCK_TENANTS];

  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(
      (t) => t.name.toLowerCase().includes(q) || t.contactEmail.toLowerCase().includes(q)
    );
  }
  if (status) {
    filtered = filtered.filter((t) => t.status === status);
  }
  if (plan) {
    filtered = filtered.filter((t) => t.plan === plan);
  }

  // Sort
  filtered.sort((a, b) => {
    const aVal = a[sortBy] || '';
    const bVal = b[sortBy] || '';
    if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  // Paginate
  const total = filtered.length;
  const start = (page - 1) * pageSize;
  const data = filtered.slice(start, start + pageSize);

  return {
    data,
    pagination: {
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
    },
  };
};

/**
 * GET /v1/tenants/:id
 * Get full tenant detail including lifecycle history
 */
export const getById = async (id) => {
  // TODO: Replace with real API call
  // return axiosInstance.get(`/tenants/${id}`);

  await delay(300);

  const summary = MOCK_TENANTS.find((t) => t.id === id);
  if (!summary) {
    throw { response: { status: 404, data: { message: 'Tenant not found' } } };
  }

  return { data: { ...MOCK_TENANT_DETAIL, id, name: summary.name, status: summary.status, plan: summary.plan } };
};

/**
 * POST /v1/tenants
 * Create and begin provisioning a new tenant
 */
export const create = async (tenantData) => {
  // TODO: Replace with real API call
  // return axiosInstance.post('/tenants', tenantData);

  await delay(800);

  const newTenant = {
    id: 'generated_' + Date.now(),
    ...tenantData,
    status: 'provisioning',
    tenantId: 'T-' + String(MOCK_TENANTS.length + 1).padStart(3, '0'),
    createdAt: new Date().toISOString(),
    activeUsers: 0,
    healthScore: 0,
  };

  return { data: newTenant };
};

/**
 * PATCH /v1/tenants/:id
 * Update tenant metadata or configuration
 */
export const update = async (id, updateData) => {
  // TODO: Replace with real API call
  // return axiosInstance.patch(`/tenants/${id}`, updateData);

  await delay(400);

  const tenant = MOCK_TENANTS.find((t) => t.id === id);
  if (!tenant) {
    throw { response: { status: 404, data: { message: 'Tenant not found' } } };
  }

  return { data: { ...tenant, ...updateData, lastModified: new Date().toISOString() } };
};

/**
 * POST /v1/tenants/:id/suspend
 * Suspend a tenant with reason and notification
 */
export const suspend = async (id, reason) => {
  // TODO: Replace with real API call
  // return axiosInstance.post(`/tenants/${id}/suspend`, { reason });

  await delay(600);

  const tenant = MOCK_TENANTS.find((t) => t.id === id);
  if (!tenant) {
    throw { response: { status: 404, data: { message: 'Tenant not found' } } };
  }
  if (tenant.status !== 'active') {
    throw { response: { status: 409, data: { message: `Cannot suspend tenant in '${tenant.status}' state. Must be 'active'.` } } };
  }

  return { data: { ...tenant, status: 'suspended', suspendedAt: new Date().toISOString() } };
};

/**
 * POST /v1/tenants/:id/reactivate
 * Reactivate a suspended tenant
 */
export const reactivate = async (id) => {
  // TODO: Replace with real API call
  // return axiosInstance.post(`/tenants/${id}/reactivate`);

  await delay(600);

  const tenant = MOCK_TENANTS.find((t) => t.id === id);
  if (!tenant) {
    throw { response: { status: 404, data: { message: 'Tenant not found' } } };
  }
  if (tenant.status !== 'suspended') {
    throw { response: { status: 409, data: { message: `Cannot reactivate tenant in '${tenant.status}' state. Must be 'suspended'.` } } };
  }

  return { data: { ...tenant, status: 'active' } };
};

/**
 * POST /v1/tenants/:id/offboard
 * Initiate offboarding workflow
 */
export const offboard = async (id, options = {}) => {
  // TODO: Replace with real API call
  // return axiosInstance.post(`/tenants/${id}/offboard`, options);

  await delay(600);

  const tenant = MOCK_TENANTS.find((t) => t.id === id);
  if (!tenant) {
    throw { response: { status: 404, data: { message: 'Tenant not found' } } };
  }
  if (!['active', 'suspended'].includes(tenant.status)) {
    throw { response: { status: 409, data: { message: `Cannot offboard tenant in '${tenant.status}' state.` } } };
  }

  return { data: { ...tenant, status: 'offboarding', offboardedAt: new Date().toISOString() } };
};

/**
 * GET /v1/tenants/:id/audit
 * Paginated audit log for a specific tenant
 */
export const getAuditLog = async (id, params = {}) => {
  // TODO: Replace with real API call
  // return axiosInstance.get(`/tenants/${id}/audit`, { params });

  await delay(300);

  return {
    data: [
      { id: '1', action: 'tenant.created', actor: 'admin@vivre.io', timestamp: '2024-08-15T10:30:00Z', details: 'Tenant created' },
      { id: '2', action: 'tenant.provisioned', actor: 'system', timestamp: '2024-08-15T10:45:00Z', details: 'Provisioning completed' },
      { id: '3', action: 'config.updated', actor: 'ops@vivre.io', timestamp: '2025-05-20T14:22:00Z', details: 'Feature flags updated' },
    ],
    pagination: { total: 3, page: 1, pageSize: 25, totalPages: 1 },
  };
};
